/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
    object, func, string, bool,
} from 'prop-types';
import mbpLogger from 'mbp-logger';

import CardMedia from '@material-ui/core/CardMedia';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import Close from '@material-ui/icons/Close';
import { bindActionCreators } from 'redux';

import { pmallPluginConfig, getPmallURL } from '../../../../../../../state/ducks/Common/ducks/PmallPersonalization/PmallPersonalization-Helper';
import * as tagManagerActions from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { removePmallItem, setPmallPersonalizationData, updateCartPmallPersonalization } from '../../../../../../../state/ducks/Common/ducks/PmallPersonalization/PmallPersonalization-Actions';
import {  getPmallSummaryInfo } from '../../../../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import { getFeatureFlags } from '../../../../../../../state/ducks/MfeCheckout/ducks/App/App-Selectors';
import { getSSRDeviceType } from '../../../../../../../state/ducks/App/App-Selectors';

const styles = (theme) => ({

    dialogPaper: {
        margin: 'unset', // default margin too big for mobile so remove
    },

    cardMediaRoot: {
        height: '90vh',
        width: '980px',
        border: 'none',
        maxHeight: '80vh',

        [theme.breakpoints.down('lg')]: {
            width: '800px',
        },

        [theme.breakpoints.down('md')]: {
            width: '575px',
        },

        [theme.breakpoints.down('sm')]: {
            width: '400px',
        },

        [theme.breakpoints.down('xs')]: {
            width: '346px',
        },
    },

    closeButtonRoot: {
        '&:hover, &:focus, &:active': {
            cursor: 'pointer',
        },
    },
});

const PmallPersonalization = (props) => {
    const {
        classes,
        featureFlags,
        pmallSummaryInfo,
        sku,
        pageType,
        isOpen,
        handleModalPmall,
        trackEvent,
        callRemovePmallItem,
        callSetPmallData,
        cartItemId,
        callUpdateCartPmallPersonalization,
        ssrDeviceType,
        refnumCheckout,
        orderItem,
    } = props;
    if (!featureFlags['is-pmall-enabled']) {
        return null;
    }

    const refnum = pmallSummaryInfo?.customizations?.refnum || refnumCheckout;
    const brand = orderItem.product?.brandCode;
    const url = getPmallURL({ sku, refnum, brand });

    /**
     * Wrap this in catch as its 3rd party and maybe
     * iframe stops sending data or errors out iteself?
     */
    const pmallistener = (event) => {
        const { sdkURL } = pmallPluginConfig;
        if (event.origin !== `https://${sdkURL}`) return;

        let parsedData;
        try {
            parsedData = JSON.parse(event.data);

            if (parsedData?.action === 'savemodal' && parsedData.postdata && parsedData.postdata.refnum === orderItem?.personalization?.personalizationId) {
                callSetPmallData(parsedData.postdata);
                if (pageType) {
                    const pmallData = {
                        cartItemId,
                        refnum: parsedData.postdata.refnum,
                        description: parsedData.postdata.description,
                        previewUrl: parsedData.postdata.imageURL,
                        onCloseModal: handleModalPmall,
                        pageType,
                    };
                    if (parsedData.postdata?.addonCount >= 0) {
                        pmallData.addonCount = parsedData.postdata.addonCount;
                    }

                    callUpdateCartPmallPersonalization(pmallData);

                    if (window.addEventListener) {
                        window.removeEventListener('message', pmallistener, false);
                    } else if (window.detachEvent) {
                        window.detachEvent('onmessage', pmallistener);
                    }
                }
            }
        } catch (error) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                component: 'PmallDialog.js',
                message: 'Error parsing Pmall iFrame data',
                error,
            });
            parsedData = { postdata: { refnum: '' } };
            callRemovePmallItem();
            handleModalPmall(false);
        }
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (window.addEventListener) {
                window.removeEventListener('message', pmallistener, false);
                window.addEventListener('message', pmallistener, false);
            } else {
                window.detachEvent('onmessage', pmallistener);
                window.attachEvent('onmessage', pmallistener);
            }
        }
    }, []);

    const handleCloseModalPersonalization = () => {
        if (window.addEventListener) {
            window.removeEventListener('message', pmallistener, false);
        } else if (window.detachEvent) {
            window.detachEvent('onmessage', pmallistener);
        }
        handleModalPmall(false);
    };
    /**
     * Setting maxWidth to false applied this style:
     *      max-width: calc(100% - 64px)
     * which is breaking mobile. Conditionally pass it using a variable
     * depending on SSR typpe.
     */
    const shouldUseMaxWidth = ssrDeviceType === 'mobile';

    return (
        <Dialog
            id={`modal-pmall-${cartItemId}`}
            open={isOpen}
            maxWidth={shouldUseMaxWidth}
            disableBackdropClick // disable close outside for only clean the edit changes is the user close using the X button
            scroll="paper"
            onEntered={() => {
                trackEvent({
                    eventCategory: 'Checkout',
                    eventAction: 'PersonalizationMall Modal',
                    eventLabel: !refnum ? 'Open' : 'Edit',
                });
            }}
            classes={{
                paper: classes.dialogPaper,
            }}
        >
            <DialogActions>
                <Close
                    id="close-pmall-dialog-button"
                    data-testid="close-pmall-dialog-button"
                    className={classes.closeButtonRoot}
                    onClick={handleCloseModalPersonalization}
                />
            </DialogActions>
            <CardMedia
                id={`iframe-${cartItemId}`}
                className={classes.cardMediaRoot}
                component="iframe"
                src={url}
            />
        </Dialog>
    );
};

PmallPersonalization.propTypes = {
    ssrDeviceType: string.isRequired,
    featureFlags: object.isRequired,
    pmallSummaryInfo: object.isRequired,
    isOpen: bool.isRequired,
    sku: string.isRequired,
    classes: object.isRequired,
    pageType: string,
    trackEvent: func.isRequired,
    callRemovePmallItem: func.isRequired,
    callSetPmallData: func.isRequired,
    handleModalPmall: func.isRequired,
    cartItemId: string,
    callUpdateCartPmallPersonalization: func.isRequired,
    refnumCheckout: string,
    orderItem: object.isRequired,
};

PmallPersonalization.defaultProps = {
    pageType: 'pdp', // pdp or checkout
    cartItemId: null,
    refnumCheckout: null,
};

const mapStateToProps = (state) => ({   // eslint-disable-line arrow-parens
    ssrDeviceType: getSSRDeviceType(state),
    featureFlags: getFeatureFlags(state),
    pmallSummaryInfo: getPmallSummaryInfo(state),
});

const mapDispatcherToProps = (dispatch) => bindActionCreators({
    callRemovePmallItem: removePmallItem,
    callSetPmallData: setPmallPersonalizationData,
    callUpdateCartPmallPersonalization: updateCartPmallPersonalization,
    trackEvent: tagManagerActions.trackEvent,
}, dispatch);

export default connect(mapStateToProps, mapDispatcherToProps)(withStyles(styles)(PmallPersonalization));
